// imports
import { createSlice } from '@reduxjs/toolkit';

// local imports
import { BASEMAPS } from '../constants/BasemapOptions';
import {
  additionalLayers,
  isGeoJsonAdditionalLayer,
  isMVTAdditionalLayer,
} from '../configs/additionalLayers';

// types
import type { ViewState } from 'react-map-gl/maplibre';

// Define an initial viewState for Maplibre map
const isMobile = window.innerWidth <= 650;

export const INITIAL_VIEW_STATE: ViewState = {
  longitude: isMobile ? -96.0 : -106.0,
  latitude: isMobile ? 38.0 : 38.5,
  zoom: isMobile ? 2 : 3.5,
  pitch: 0,
  bearing: 0,
  padding: { top: 0, bottom: 0, left: 0, right: 0 },
};

// Define the initial state of the map slice
const initialMapState = {
  basemap: BASEMAPS[0],
  viewState: INITIAL_VIEW_STATE,
  layers: additionalLayers,
  sidePanelOpen: true,
  awdVisible: true,
  primaryNavOpen: false,
};

export const mapSlice = createSlice({
  name: 'map',
  initialState: initialMapState,
  reducers: {
    setBasemap: (state, action) => {
      state.basemap = action.payload;
    },
    setViewState: (state, action) => {
      state.viewState = { ...state.viewState, ...action.payload };
    },
    setActive: (state, action) => {
      const { id, active }: { id: keyof typeof state.layers; active: boolean } =
        action.payload;
      state.layers[id] = {
        ...state.layers[id],
        active,
      };
    },
    setOpacity: (state, action) => {
      const {
        id,
        opacity,
      }: { id: keyof typeof state.layers; opacity: number } = action.payload;
      state.layers[id] = {
        ...state.layers[id],
        opacity,
      };
    },
    setBorderColor: (state, action) => {
      const {
        id,
        borderColor,
      }: { id: keyof typeof state.layers; borderColor: string } =
        action.payload;
      if (
        (!isMVTAdditionalLayer(state.layers[id]) &&
          !isGeoJsonAdditionalLayer(state.layers[id])) ||
        state.layers[id].geomType !== 'polygon'
      ) {
        return;
      }
      state.layers[id] = {
        ...state.layers[id],
        borderColor,
      };
    },
    setBorderWidth: (state, action) => {
      const {
        id,
        borderWidth,
      }: { id: keyof typeof state.layers; borderWidth: number } =
        action.payload;
      if (
        (!isMVTAdditionalLayer(state.layers[id]) &&
          !isGeoJsonAdditionalLayer(state.layers[id])) ||
        state.layers[id].geomType !== 'polygon'
      ) {
        return;
      }
      state.layers[id] = {
        ...state.layers[id],
        borderWidth,
      };
    },
    setLineColor: (state, action) => {
      const {
        id,
        lineColor,
      }: { id: keyof typeof state.layers; lineColor: string } = action.payload;
      if (
        !isGeoJsonAdditionalLayer(state.layers[id]) ||
        state.layers[id].geomType !== 'line'
      ) {
        return;
      }
      state.layers[id] = {
        ...state.layers[id],
        lineColor,
      };
    },
    setLineWidth: (state, action) => {
      const {
        id,
        lineWidth,
      }: { id: keyof typeof state.layers; lineWidth: number } = action.payload;
      if (
        !isGeoJsonAdditionalLayer(state.layers[id]) ||
        state.layers[id].geomType !== 'line'
      ) {
        return;
      }
      state.layers[id] = {
        ...state.layers[id],
        lineWidth,
      };
    },
    setPointColor: (state, action) => {
      const {
        id,
        pointColor,
      }: { id: keyof typeof state.layers; pointColor: string } = action.payload;
      if (
        (!isMVTAdditionalLayer(state.layers[id]) &&
          !isGeoJsonAdditionalLayer(state.layers[id])) ||
        state.layers[id].geomType !== 'point'
      ) {
        return;
      }
      state.layers[id] = {
        ...state.layers[id],
        pointColor,
      };
    },
    setPointSize: (state, action) => {
      const {
        id,
        pointSize,
      }: { id: keyof typeof state.layers; pointSize: number } = action.payload;
      if (
        (!isMVTAdditionalLayer(state.layers[id]) &&
          !isGeoJsonAdditionalLayer(state.layers[id])) ||
        state.layers[id].geomType !== 'point'
      ) {
        return;
      }
      state.layers[id] = {
        ...state.layers[id],
        pointSize,
      };
    },

    toggleSidePanel: (state) => {
      state.sidePanelOpen = !state.sidePanelOpen;
      // When screen size is <491px, close primary nav when side panel opens
      if (
        state.sidePanelOpen &&
        typeof window !== 'undefined' &&
        window.innerWidth < 491
      ) {
        state.primaryNavOpen = false;
      }
    },

    setPrimaryNavOpen: (state, action) => {
      state.primaryNavOpen = action.payload;
      // When screen size is <491px, close side panel when primary nav opens
      if (
        state.primaryNavOpen &&
        typeof window !== 'undefined' &&
        window.innerWidth < 491
      ) {
        state.sidePanelOpen = false;
      }
    },

    setAwdVisibility: (state, action) => {
      state.awdVisible = action.payload;
    },

    setSidePanelOpen: (state, action) => {
      state.sidePanelOpen = action.payload;
    },
  },
});

export const {
  setBasemap,
  setViewState,
  setActive,
  setOpacity,
  setBorderColor,
  setBorderWidth,
  toggleSidePanel,
  setSidePanelOpen,
  setAwdVisibility,
  setPrimaryNavOpen,
} = mapSlice.actions;
